<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import { InlineSearchBar } from '@/components/utilities/search';
import UnitSelectionWizardSteps from '@/components/forms/UnitSelectionWizardSteps.vue';
import ProjectList from '@/components/rental/projects/ProjectList.vue';
import ProjectGoogleMap from '@/components/maps_canvases/ProjectGoogleMap.vue';
import { usePurchaseFilters } from '@/composables/usePurchaseFilters';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { useProjectsApi } from '@/composables/useProjectsApi';

import { ListingTypeEnum, listingTypeEnumNumericValue } from '@/enums/ListingType.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Project } from '@/typings/projects/Project.ts';
import type { Region } from '@/typings/regions/Region.ts';
import type { GetProjectsRequest } from '@/typings/requests/GetProjectsRequest.ts';
import { DEFAULT_PURCHASE_UNIT_STATUSES, DEFAULT_PURCHASE_VALID_UNIT_STATUSES } from '@/constants/purchaseConstants';
import Map from '../svg/Map.vue';
import List from '../svg/List.vue';


const { t, locale } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});
const { getProjectsByRegion } = useProjectsApi();

const { getProjectWizardUrl, purchaseWizardFirstStepUrl } = usePurchaseLocalizedUrls();
const { loadFiltersFromPurchaseStorage } = usePurchaseFilters();

const props = defineProps<{
  unitOccupancyType: ListingTypeEnum;
  region: Region;
}>();

const projects = ref<Project[]>([]);
const hoveredProject = ref<Project>();
const showMap = ref(false);
const isMobile = ref(false);
const wizardStep = ref(2);



function onBackToRegionSelection() {
  window.location.href = purchaseWizardFirstStepUrl;
}

function onProjectSelected(project: Project) {
  // Navigation directe vers l'étape suivante
  window.location.href = getProjectWizardUrl(project);
}

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 1024;
};

const toggleListsMap = () => {
  if(isMobile.value){
    showMap.value = !showMap.value;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const loadProjects = async () => {
  try {
    const allFilters = loadFiltersFromPurchaseStorage();
    
    const request: GetProjectsRequest = {
      locale: locale.value.substring(0, 2),
      listingType: 0, // Purchase
      regionIds: [props.region.id],
      unitConfigurations: allFilters.unitConfigurations.length > 0 ? allFilters.unitConfigurations : undefined,
      availabilityDates: allFilters.availabilityDates,
      floorNumbers: allFilters.floors,
      minPrice: allFilters.minPrice,
      maxPrice: allFilters.maxPrice,
      unitStatuses: DEFAULT_PURCHASE_VALID_UNIT_STATUSES
    };
    
    const updatedProjects = await getProjectsByRegion([props.region.id], request);
    projects.value = updatedProjects.sort((a, b) => {
      // Trier d'abord par disponibilité (disponible avant non disponible)
      const aAvailable = a.availableUnits && a.availableUnits > 0 ? 1 : 0;
      const bAvailable = b.availableUnits && b.availableUnits > 0 ? 1 : 0;
      if (aAvailable !== bAvailable) {
        return bAvailable - aAvailable;
      }
      
      // Ensuite, trier par nom de projet
      return a.name.localeCompare(b.name, locale.value);
    });
  } catch (error) {
    console.error('Failed to reload projects with filters:', error);
  }
};

// Fonction pour appliquer les filtres secondaires
const applySecondaryFilters = async () => {
  // Recharger les projets avec les nouveaux filtres
  await loadProjects();
};

onMounted(async () => {
  const request: GetProjectsRequest  = {
    locale: locale.value.substring(0, 2),
    regionIds: [props.region.id],
    listingType: listingTypeEnumNumericValue[props.unitOccupancyType],
    unitStatuses: DEFAULT_PURCHASE_VALID_UNIT_STATUSES
  };
  projects.value = await getProjectsByRegion([props.region.id], request);
  
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});
</script>

<template>
  <!--Achat - Étape 2 - Choix du projet-->
  <div class="projets-by-region-purchase-browsing">
    <!-- Barre de filtres -->
    <InlineSearchBar action-type="filter"
                      :listing-type="ListingTypeEnum.Sale"
                     :selected-region="props.region.id"
                     @filtersApplied="applySecondaryFilters" />
    <div class="wrapper wrapper_large">
      <UnitSelectionWizardSteps v-model="wizardStep" />
      <div class="main-content">
        <!--En-tête avec bouton de retour-->
        <HeaderWithBackButton :title="t('browsingApp.projects.chooseTheRentalProjectDescription', { count: projects.length, city: props.region.localizedName })"
                              :subtitle="t('browsingApp.projects.chooseTheProjectDescription')"                         
                              @back-click="onBackToRegionSelection" />
        <div class="twoColumns">
          <!-- Projects content (left side on desktop, full width on mobile when map is hidden) -->
          <div v-if="!isMobile || (isMobile && !showMap)"
               class="projects-content">
            <div class="projects-list">
              <div class="projects-list-section">
                <ProjectList :projects="projects"
                             :loading="false"
                             :listingType="props.unitOccupancyType"
                             :is-selectable="true"
                             @project-selected="onProjectSelected" />
              </div>
            </div>
          </div>
          <!-- Google Map (right side on desktop, full width on mobile when showMap = true) -->
          <div v-if="!isMobile || (isMobile && showMap)"
               class="projects-map-section">
            <ProjectGoogleMap v-if="projects.length > 0"
                              :projects="projects"
                              :listing-type="props.unitOccupancyType"
                              v-model:hovered-model="hoveredProject"
                              @map-loaded="() => {}" />
          </div>
          <!-- Mobile toggle button -->
          <div v-if="isMobile"
               class="button-map-mobile"
               @click="toggleListsMap">
            <span class="icon">
              <List v-if="showMap" />
              <Map v-else />
            </span>
            <span>{{ showMap ? t('browsingApp.projects.viewList') : t('browsingApp.projects.viewMap') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
// Layout with map positioned on the right
.main-content{
  .projects-content{
    width: 65%;
  }
  
  .projects-map-section{
    width: 35%;
    display: block;
    position: sticky;
    top: 230px;
    height: calc(100vh - 252px);
  }
}

.button-map-mobile{
  position: sticky;
  bottom: 1.5rem;
  left: -9999px;
  right: -9999px;
  margin: 0 auto;
  color: #fff;
  background-color: #000;
  padding: 1.2rem 1.6rem;
  border-radius: 3rem;
  display: flex;
  gap: 0.8rem;
  align-items: center;
  cursor: pointer;
  z-index: 99;

  .icon{
    width: 1.6rem;
    height: 1.6rem;
    display: block;

    svg{
      width: 100%;
      height: 100%;
      display: block;
    }
  }
}

// Mobile responsive styles
@media screen and (max-width: 1024px){
  .main-content{
    .projects-content{
      width: 100%;
    }

    .projects-map-section{
      width: 100%;
      position: relative;
      top: inherit;
      height: 100vh;
    }
  }
}
</style>
